import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import MvCard from "@/components/feature/MvCard";
import { genres, mvList, siteStats } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";
import { formatViews } from "@/utils/format";

export default function Home() {
  useSeo({
    title: "红桃MV视频官网 - 最新MV视频观看平台",
    description:
      "红桃MV视频聚合丰富的音乐视频资源，涵盖多种风格与热门榜单，用户可在线欣赏最新MV视频及相关资讯。",
    keywords: "红桃MV视频,MV视频,音乐视频,最新MV,视频平台,MV观看,红桃音乐",
  });

  const latest = [...mvList].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1)).slice(0, 8);
  const hot = [...mvList].sort((a, b) => b.trending - a.trending).slice(0, 8);

  return (
    <div data-product-shop className="w-full">
      <HeroCarousel />

      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">音乐风格</h2>
            <p className="mt-1 text-sm text-foreground-400">按风格快速找到你喜欢的 MV</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {genres.map((g) => (
            <Link
              key={g.code}
              to={`/category/${g.code}`}
              className="group flex items-center gap-4 rounded-lg border border-background-800 bg-background-900 p-4 transition-colors hover:border-primary-500/60 hover:bg-background-800"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-500/15 text-2xl text-primary-400 transition-colors group-hover:bg-accent-500 group-hover:text-background-950">
                <i className={g.icon} aria-hidden="true"></i>
              </span>
              <div className="min-w-0">
                <h3 className="truncate text-sm font-medium text-foreground-100">{g.name}</h3>
                <p className="mt-0.5 truncate text-xs text-foreground-500">{g.desc.slice(0, 12)}…</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-900/50 px-4 py-12 md:px-8 md:py-16">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">新片推荐</h2>
            <p className="mt-1 text-sm text-foreground-400">最新上线的 MV，第一时间为你呈现</p>
          </div>
          <Link to="/charts" className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-400 transition-colors hover:text-primary-300">
            查看全部
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {latest.map((mv) => (
            <MvCard key={mv.id} mv={mv} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">热门排行榜</h2>
            <p className="mt-1 text-sm text-foreground-400">当下人气最高的 MV，正在被反复播放</p>
          </div>
          <Link to="/charts" className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-400 transition-colors hover:text-primary-300">
            完整榜单
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {hot.map((mv, i) => (
            <Link
              key={mv.id}
              to={`/detail/${mv.id}`}
              className="group flex items-center gap-4 rounded-lg border border-background-800 bg-background-900 p-3 transition-colors hover:border-primary-500/60"
            >
              <span className={`w-8 shrink-0 text-center font-heading text-xl font-bold ${i < 3 ? "text-accent-500" : "text-foreground-500"}`}>
                {i + 1}
              </span>
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md">
                <img src={mv.cover} alt={`${mv.title} 缩略图`} title={`${mv.title} - ${mv.artist}`} className="h-full w-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-400">{mv.title}</h3>
                <p className="mt-1 truncate text-xs text-foreground-400">{mv.artist}</p>
              </div>
              <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500">
                <i className="ri-play-circle-line" aria-hidden="true"></i>
                {formatViews(mv.views)}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-900/50 px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
              关于 <span className="text-primary-400">红桃MV视频</span>
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-300">
              红桃MV视频是一个专注音乐视频的聚合与观看平台。我们持续收录全球最新、最热门的 MV，覆盖流行、摇滚、说唱、电子、国风等多元风格，用清晰的分类、权威的榜单与贴心的推荐，帮你高效发现真正喜欢的内容。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {siteStats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-800 bg-background-900 p-3 text-center">
                  <p className="font-heading text-xl font-bold text-accent-500">{s.value}</p>
                  <p className="mt-1 text-xs text-foreground-400">{s.label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {mvList.slice(0, 4).map((mv) => (
              <Link key={mv.id} to={`/detail/${mv.id}`} className="group relative aspect-video overflow-hidden rounded-lg">
                <img src={mv.cover} alt={`${mv.title} 海报`} title={`${mv.title} - ${mv.artist}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 to-transparent"></div>
                <div className="absolute bottom-2 left-2">
                  <p className="truncate text-xs font-medium text-foreground-100">{mv.title}</p>
                  <p className="truncate text-[11px] text-foreground-400">{mv.artist}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}